import { useMemo } from 'react';
import type { Appointment, AppointmentType } from '../../../mocks/appointments';

interface RevenueSummaryProps {
  appointments: Appointment[];
  types: AppointmentType[];
}

export default function RevenueSummary({ appointments, types }: RevenueSummaryProps) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  const paidAppointments = appointments.filter((a) => a.paid);

  // Helpers
  const getPrice = (serviceType: string) => {
    const type = types.find((t) => t.name === serviceType);
    return type?.price ?? 0;
  };

  const isInMonth = (dateStr?: string, year?: number, month?: number) => {
    if (!dateStr || year === undefined || month === undefined) return false;
    const d = new Date(dateStr + 'T00:00:00');
    return d.getFullYear() === year && d.getMonth() === month;
  };

  // Current month
  const currentMonthPaid = useMemo(
    () =>
      paidAppointments.filter((a) => isInMonth(a.preferred_date, currentYear, currentMonth)),
    [paidAppointments, currentYear, currentMonth]
  );
  const currentMonthTotal = useMemo(
    () => currentMonthPaid.reduce((sum, a) => sum + getPrice(a.service_type), 0),
    [currentMonthPaid]
  );

  // Previous month
  const prevMonthPaid = useMemo(
    () =>
      paidAppointments.filter((a) => isInMonth(a.preferred_date, prevYear, prevMonth)),
    [paidAppointments, prevYear, prevMonth]
  );
  const prevMonthTotal = useMemo(
    () => prevMonthPaid.reduce((sum, a) => sum + getPrice(a.service_type), 0),
    [prevMonthPaid]
  );

  // Pending payments
  const pendingAppointments = useMemo(
    () => appointments.filter((a) => !a.paid && getPrice(a.service_type) > 0),
    [appointments]
  );
  const pendingTotal = useMemo(
    () => pendingAppointments.reduce((sum, a) => sum + getPrice(a.service_type), 0),
    [pendingAppointments]
  );

  // Revenue by service type
  const byService = useMemo(() => {
    const map: Record<string, { count: number; total: number }> = {};
    paidAppointments.forEach((a) => {
      const price = getPrice(a.service_type);
      if (price <= 0) return;
      if (!map[a.service_type]) {
        map[a.service_type] = { count: 0, total: 0 };
      }
      map[a.service_type].count += 1;
      map[a.service_type].total += price;
    });
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  }, [paidAppointments, types]);

  // Total all time
  const totalAllTime = useMemo(
    () => paidAppointments.reduce((sum, a) => sum + getPrice(a.service_type), 0),
    [paidAppointments]
  );

  const percentChange = prevMonthTotal === 0
    ? currentMonthTotal > 0 ? 100 : 0
    : ((currentMonthTotal - prevMonthTotal) / prevMonthTotal) * 100;

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  return (
    <div className="space-y-6">
      {/* Main KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Current month revenue */}
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col">
          <span className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium">
            Ingresos {monthNames[currentMonth]}
          </span>
          <span className="font-serif text-brand-black text-2xl font-semibold mt-2">
            €{currentMonthTotal.toFixed(2)}
          </span>
          <div className="flex items-center gap-1 mt-2">
            {percentChange >= 0 ? (
              <>
                <i className="ri-arrow-up-line text-emerald-600 text-sm" />
                <span className="font-sans text-emerald-600 text-xs font-medium">
                  {percentChange === 100 && prevMonthTotal === 0 ? 'Nuevo' : `+${percentChange.toFixed(1)}%`}
                </span>
              </>
            ) : (
              <>
                <i className="ri-arrow-down-line text-red-500 text-sm" />
                <span className="font-sans text-red-500 text-xs font-medium">
                  {percentChange.toFixed(1)}%
                </span>
              </>
            )}
            <span className="font-sans text-brand-gray-400 text-xs">
              vs {monthNames[prevMonth]}
            </span>
          </div>
        </div>

        {/* Previous month revenue */}
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col">
          <span className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium">
            Ingresos {monthNames[prevMonth]}
          </span>
          <span className="font-serif text-brand-black text-2xl font-semibold mt-2">
            €{prevMonthTotal.toFixed(2)}
          </span>
          <span className="font-sans text-brand-gray-400 text-xs mt-2">
            {prevMonthPaid.length} citas pagadas
          </span>
        </div>

        {/* Pending payments */}
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col">
          <span className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium">
            Pendiente de cobro
          </span>
          <span className="font-serif text-brand-black text-2xl font-semibold mt-2">
            €{pendingTotal.toFixed(2)}
          </span>
          <span className="font-sans text-brand-gray-400 text-xs mt-2">
            {pendingAppointments.length} citas sin pagar
          </span>
        </div>

        {/* Total all time */}
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5 flex flex-col">
          <span className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium">
            Total histórico
          </span>
          <span className="font-serif text-brand-black text-2xl font-semibold mt-2">
            €{totalAllTime.toFixed(2)}
          </span>
          <span className="font-sans text-brand-gray-400 text-xs mt-2">
            {paidAppointments.length} pagos confirmados
          </span>
        </div>
      </div>

      {/* Breakdown by service */}
      {byService.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <h3 className="font-serif text-brand-black text-lg font-medium mb-5">
            Ingresos por servicio
          </h3>
          <div className="space-y-4">
            {byService.map(([serviceName, data]) => {
              const pct = totalAllTime > 0 ? (data.total / totalAllTime) * 100 : 0;
              return (
                <div key={serviceName}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-sans text-brand-black text-sm font-medium">
                      {serviceName}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="font-sans text-brand-gray-500 text-xs">
                        {data.count} {data.count === 1 ? 'cita' : 'citas'}
                      </span>
                      <span className="font-sans text-brand-black text-sm font-medium">
                        €{data.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-brand-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-black rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-sans text-brand-gray-400 text-xs mt-1">
                    {pct.toFixed(1)}% del total
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent paid transactions */}
      {currentMonthPaid.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-brand-gray-100">
            <h3 className="font-serif text-brand-black text-lg font-medium">
              Pagos de {monthNames[currentMonth]}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-brand-gray-50">
                <tr>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                    Cliente
                  </th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                    Servicio
                  </th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                    Fecha cita
                  </th>
                  <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                    Importe
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-gray-200">
                {currentMonthPaid.slice(0, 10).map((a) => (
                  <tr key={a.id} className="hover:bg-brand-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="font-sans text-brand-black text-sm font-medium">{a.name}</p>
                      <p className="font-sans text-brand-gray-400 text-xs">{a.email}</p>
                    </td>
                    <td className="px-5 py-3 font-sans text-brand-gray-600 text-sm">
                      {a.service_type}
                    </td>
                    <td className="px-5 py-3 font-sans text-brand-black text-sm">
                      {a.preferred_date}
                    </td>
                    <td className="px-5 py-3 text-right font-sans text-brand-black text-sm font-medium">
                      €{getPrice(a.service_type).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {currentMonthPaid.length > 10 && (
            <p className="px-5 py-3 font-sans text-brand-gray-400 text-xs text-center border-t border-brand-gray-100">
              +{currentMonthPaid.length - 10} pagos más este mes
            </p>
          )}
        </div>
      )}

      {paidAppointments.length === 0 && (
        <div className="bg-brand-gray-50 rounded-2xl p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-3">
            <i className="ri-bar-chart-line text-brand-gray-400 text-xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            Aún no hay pagos registrados. Los ingresos aparecerán aquí cuando los clientes completen el pago online.
          </p>
        </div>
      )}
    </div>
  );
}